import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import authService from './authService'

const user = typeof window !== 'undefined' && localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null

const initialState = {
  user: user ? user : null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: '',
  passwordResetRequest: {
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: '',
  },
  updatePreferencesRequest: {
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: '',
  },
}

export const registerUser = createAsyncThunk('auth/registerUser', async (userData, thunkAPI) => {
  const { email, password, inviteCode, promotionalEmailsConsent } = userData.formData

  const requestData = { email, password, inviteCode, promotionalEmailsConsent }

    try {
      return await authService.registerUser(requestData)
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
      // console.log(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const loginUser = createAsyncThunk('auth/loginUser', async (userData, thunkAPI) => {
  try {
    return await authService.loginUser(userData.formData)
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
    // console.log(error)
    return thunkAPI.rejectWithValue(message)
  }
})

export const getUserObject = createAsyncThunk('auth/getUserObject', async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.user.token
    return await authService.getUserObject(token)
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
    // console.log(error)
    return thunkAPI.rejectWithValue(message)
  }
})

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  await authService.logoutUser()
})

export const verifyEmail = createAsyncThunk('auth/verifyEmail', async (verifyData, thunkAPI) => {
  try {
    return await authService.verifyEmail(verifyData)
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
    // console.log(error)
    return thunkAPI.rejectWithValue(message)
  }
})

export const requestPasswordResetEmail = createAsyncThunk('auth/requestPasswordResetEmail', async (formData, thunkAPI) => {
  try {
    return await authService.requestPasswordResetEmail(formData)
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
    // console.log(error)
    return thunkAPI.rejectWithValue(message)
  }
})

export const forgottenPasswordReset = createAsyncThunk('auth/forgottenPasswordReset', async (formData, thunkAPI) => {
  try {
    return await authService.forgottenPasswordReset(formData.formData)
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        
    // console.log(error)
    return thunkAPI.rejectWithValue(message)
  }
})

export const updateUserPreferences = createAsyncThunk('auth/updateUserPreferences', async (preferencesData, thunkAPI) => {
  try {
      const token = thunkAPI.getState().auth.user.token
      return await authService.updateUserPreferences(preferencesData, token)
  } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()

      // console.log(error)
      return thunkAPI.rejectWithValue(message)
  }
})

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false
      state.isSuccess = false
      state.isError = false
      state.message = ''
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.user = action.payload
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
        state.user = null
      })

      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.user = action.payload
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
        state.user = null
      })

      .addCase(getUserObject.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getUserObject.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.user = action.payload
      })
      .addCase(getUserObject.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
        state.user = null
      })

      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false
        state.isSuccess = true
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      .addCase(requestPasswordResetEmail.pending, (state) => {
        state.passwordResetRequest.isLoading = true
      })
      .addCase(requestPasswordResetEmail.fulfilled, (state, action) => {
        state.passwordResetRequest.isLoading = false
        state.passwordResetRequest.isSuccess = true
      })
      .addCase(requestPasswordResetEmail.rejected, (state, action) => {
        state.passwordResetRequest.isLoading = false
        state.passwordResetRequest.isError = true
        state.passwordResetRequest.message = action.payload
      })

      .addCase(forgottenPasswordReset.pending, (state) => {
        state.passwordResetRequest.isLoading = true
      })
      .addCase(forgottenPasswordReset.fulfilled, (state, action) => {
        state.passwordResetRequest.isLoading = false
        state.passwordResetRequest.isSuccess = true
      })
      .addCase(forgottenPasswordReset.rejected, (state, action) => {
        state.passwordResetRequest.isLoading = false
        state.passwordResetRequest.isError = true
        state.passwordResetRequest.message = action.payload
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null
      })

      .addCase(updateUserPreferences.pending, (state) => {
        state.updatePreferencesRequest.isLoading = true
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.updatePreferencesRequest.isLoading = false
        state.updatePreferencesRequest.isSuccess = true
        state.user = action.payload
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.updatePreferencesRequest.isLoading = false
        state.updatePreferencesRequest.isError = true
        state.updatePreferencesRequest.message = action.payload
      })
  },
})

export const { reset } = authSlice.actions
export default authSlice.reducer